import { useState, useEffect, useRef } from 'react';

const scanItems = [
  { id: 1, label: 'Wing surface', status: 'ok', icon: 'ri-checkbox-circle-line' },
  { id: 2, label: 'Control surface hinge', status: 'ok', icon: 'ri-checkbox-circle-line' },
  { id: 3, label: 'Fuel cap secured', status: 'ok', icon: 'ri-checkbox-circle-line' },
  { id: 4, label: 'Static port detected', status: 'ok', icon: 'ri-checkbox-circle-line' },
  { id: 5, label: 'Surface irregularity', status: 'warn', icon: 'ri-error-warning-line' },
];

const overlayLabels = [
  { x: 62, y: 38, text: 'Aileron', color: 'cyan' },
  { x: 30, y: 55, text: 'Fuel Cap', color: 'cyan' },
  { x: 78, y: 62, text: 'Flap Hinge', color: 'green' },
  { x: 18, y: 35, text: 'Pitot Tube', color: 'cyan' },
  { x: 50, y: 72, text: 'Main Gear', color: 'green' },
];

export default function FeaturePreflightInspection() {
  const [scanProgress, setScanProgress] = useState(0);
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const [showAlert, setShowAlert] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let progress = 0;
    const interval = setInterval(() => {
      progress += 1;
      setScanProgress(progress);
      if (progress === 20) setVisibleItems([1]);
      if (progress === 40) setVisibleItems([1, 2]);
      if (progress === 55) setVisibleItems([1, 2, 3]);
      if (progress === 68) setVisibleItems([1, 2, 3, 4]);
      if (progress === 78) {
        setVisibleItems([1, 2, 3, 4, 5]);
        setShowAlert(true);
      }
      if (progress >= 100) clearInterval(interval);
    }, 60);
    return () => clearInterval(interval);
  }, [isVisible]);

  const resetAnimation = () => {
    setScanProgress(0);
    setVisibleItems([]);
    setShowAlert(false);
    setTimeout(() => setIsVisible(true), 100);
  };

  return (
    <section
      id="preflight-inspection"
      ref={sectionRef}
      className="relative py-20 sm:py-28 overflow-hidden bg-[#060E1A]"
    >
      {/* Background grid */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'linear-gradient(#00D4FF 1px, transparent 1px), linear-gradient(90deg, #00D4FF 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }}></div>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00FFD1]/30 to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00FFD1]/30 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full px-4 sm:px-5 py-2 mb-4 sm:mb-6">
            <i className="ri-scan-line text-[#00FFD1] text-sm"></i>
            <span className="text-[#00FFD1] text-xs sm:text-sm font-medium uppercase tracking-widest">AI Preflight Inspection</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-5 leading-tight">
            See what the eye<br />
            <span className="text-[#00FFD1]">might miss.</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Point your phone at the aircraft during preflight. WingmanFix scans the surface, labels key components, and flags anything worth a second look — before you ever leave the ground.
          </p>
        </div>

        {/* Main visual - stacks vertically on mobile */}
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-10 items-center">

          {/* LEFT: Scene visual */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden border border-[#00D4FF]/20 shadow-2xl shadow-[#00D4FF]/10">
              {/* Background scene image */}
              <img
                src="https://public.readdy.ai/ai/img_res/7735fa37ac0c2e64091ea9c05dad6f33.jpg"
                alt="Aircraft on ramp at sunrise"
                className="w-full h-[300px] sm:h-[420px] object-cover object-top"
              />

              {/* Dark overlay for contrast */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#060E1A]/80 via-[#060E1A]/20 to-transparent"></div>

              {/* Scanning grid overlay */}
              {isVisible && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: 'linear-gradient(rgba(0,212,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.08) 1px, transparent 1px)',
                    backgroundSize: '28px 28px',
                    animation: 'pulse 3s ease-in-out infinite',
                  }}
                ></div>
              )}

              {/* Scan sweep line */}
              {isVisible && scanProgress < 100 && (
                <div
                  className="absolute left-0 right-0 h-0.5 pointer-events-none"
                  style={{
                    top: `${scanProgress}%`,
                    background: 'linear-gradient(90deg, transparent, #00D4FF, #00FFD1, #00D4FF, transparent)',
                    boxShadow: '0 0 12px 3px rgba(0,212,255,0.6)',
                    transition: 'top 0.06s linear',
                  }}
                ></div>
              )}

              {/* Holographic component labels */}
              {isVisible && overlayLabels.map((label, i) => (
                <div
                  key={i}
                  className="absolute pointer-events-none"
                  style={{
                    left: `${label.x}%`,
                    top: `${label.y}%`,
                    opacity: scanProgress > label.x ? 1 : 0,
                    transition: 'opacity 0.5s ease',
                  }}
                >
                  <div className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-mono font-semibold whitespace-nowrap ${
                    label.color === 'green'
                      ? 'bg-emerald-500/20 border border-emerald-400/50 text-emerald-300'
                      : 'bg-[#00D4FF]/15 border border-[#00D4FF]/50 text-[#00D4FF]'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${label.color === 'green' ? 'bg-emerald-400' : 'bg-[#00D4FF]'}`}></span>
                    {label.text}
                  </div>
                  <div className={`w-px h-4 ml-3 ${label.color === 'green' ? 'bg-emerald-400/50' : 'bg-[#00D4FF]/50'}`}></div>
                </div>
              ))}

              {/* Red detection box */}
              {showAlert && (
                <div
                  className="absolute pointer-events-none"
                  style={{ left: '54%', top: '30%', width: '18%', height: '22%' }}
                >
                  <div
                    className="w-full h-full border-2 border-red-500 rounded-sm"
                    style={{
                      boxShadow: '0 0 16px 4px rgba(239,68,68,0.4), inset 0 0 16px rgba(239,68,68,0.1)',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                  ></div>
                  {/* Corner ticks */}
                  <div className="absolute -top-1 -left-1 w-3 h-3 border-l-2 border-t-2 border-red-400"></div>
                  <div className="absolute -top-1 -right-1 w-3 h-3 border-r-2 border-t-2 border-red-400"></div>
                  <div className="absolute -bottom-1 -left-1 w-3 h-3 border-l-2 border-b-2 border-red-400"></div>
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 border-r-2 border-b-2 border-red-400"></div>
                  {/* Alert label */}
                  <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-red-600/90 border border-red-400 rounded px-2 py-1 text-xs font-mono text-white whitespace-nowrap shadow-lg">
                    <div className="font-bold text-red-200">⚠ Inspection recommended</div>
                    <div className="text-red-300 text-[10px]">Surface irregularity detected</div>
                  </div>
                </div>
              )}

              {/* Phone mockup overlay */}
              <div className="absolute bottom-3 sm:bottom-5 right-3 sm:right-5 w-24 sm:w-28">
                <div className="bg-[#0A1628]/90 border border-[#00D4FF]/40 rounded-2xl p-1.5 sm:p-2 shadow-xl shadow-[#00D4FF]/20">
                  <div className="bg-[#060E1A] rounded-xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=smartphone%20screen%20showing%20aviation%20app%20UI%20dark%20theme%20with%20scan%20interface%20blue%20holographic%20overlay%20aircraft%20inspection%20digital%20interface%20close%20up&width=200&height=340&seq=phone-screen-preflight-001&orientation=portrait"
                      alt="WingmanFix app screen"
                      className="w-full h-28 sm:h-36 object-cover object-top"
                    />
                    <div className="p-1.5 sm:p-2">
                      <div className="text-[8px] sm:text-[9px] text-[#00FFD1] font-mono font-bold mb-1">SCANNING...</div>
                      <div className="w-full bg-[#0F1F35] rounded-full h-1 mb-1">
                        <div
                          className="h-1 rounded-full bg-gradient-to-r from-[#00D4FF] to-[#00FFD1] transition-all duration-300"
                          style={{ width: `${scanProgress}%` }}
                        ></div>
                      </div>
                      <div className="text-[8px] sm:text-[9px] text-gray-400 font-mono">{scanProgress}% complete</div>
                    </div>
                  </div>
                </div>
                {/* Scan beam from phone */}
                <div
                  className="absolute top-6 sm:top-8 -left-12 sm:-left-16 w-12 sm:w-16 h-0.5 pointer-events-none"
                  style={{
                    background: 'linear-gradient(90deg, rgba(0,212,255,0.6), transparent)',
                    boxShadow: '0 0 6px rgba(0,212,255,0.4)',
                  }}
                ></div>
              </div>

              {/* HUD corners */}
              <div className="absolute top-3 left-3 w-6 h-6 sm:w-8 sm:h-8 border-l-2 border-t-2 border-[#00FFD1]/40"></div>
              <div className="absolute top-3 right-3 w-6 h-6 sm:w-8 sm:h-8 border-r-2 border-t-2 border-[#00FFD1]/40"></div>
              <div className="absolute bottom-3 left-3 w-6 h-6 sm:w-8 sm:h-8 border-l-2 border-b-2 border-[#00FFD1]/40"></div>
              <div className="absolute bottom-3 right-3 w-6 h-6 sm:w-8 sm:h-8 border-r-2 border-b-2 border-[#00FFD1]/40"></div>

              {/* Scan progress badge */}
              <div className="absolute top-3 sm:top-4 left-1/2 -translate-x-1/2 bg-[#0A1628]/80 border border-[#00D4FF]/40 rounded-full px-3 sm:px-4 py-1 sm:py-1.5 flex items-center gap-1.5 sm:gap-2">
                <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-[#00FFD1] animate-pulse"></span>
                <span className="text-[10px] sm:text-xs font-mono text-[#00D4FF] font-semibold">SCAN PROGRESS {scanProgress}%</span>
              </div>
            </div>

            {/* Replay button */}
            <button
              onClick={resetAnimation}
              className="mt-3 sm:mt-4 flex items-center gap-2 text-xs text-gray-400 hover:text-[#00FFD1] transition-colors cursor-pointer mx-auto"
            >
              <i className="ri-refresh-line"></i>
              Replay scan animation
            </button>
          </div>

          {/* RIGHT: Info panel */}
          <div className="flex flex-col gap-4 sm:gap-6">

            {/* Scan checklist */}
            <div className="bg-[#0A1628]/80 border border-[#00D4FF]/20 rounded-2xl p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center">
                    <i className="ri-list-check-3 text-[#00FFD1] text-lg sm:text-xl"></i>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-white uppercase tracking-wider">Live Scan Results</span>
                </div>
                <div className="flex items-center gap-2 bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-full px-2 sm:px-3 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00D4FF] animate-pulse"></span>
                  <span className="text-xs text-[#00D4FF] font-mono">ACTIVE</span>
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                {scanItems.map((item) => {
                  const visible = visibleItems.includes(item.id);
                  return (
                    <div
                      key={item.id}
                      className={`flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-xl border transition-all duration-500 ${
                        visible
                          ? item.status === 'warn'
                            ? 'bg-red-500/10 border-red-500/40'
                            : 'bg-emerald-500/10 border-emerald-500/30'
                          : 'bg-[#0F1F35]/50 border-gray-700/30 opacity-40'
                      }`}
                    >
                      <div className={`w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full ${
                        visible
                          ? item.status === 'warn'
                            ? 'bg-red-500/20'
                            : 'bg-emerald-500/20'
                          : 'bg-gray-700/30'
                      }`}>
                        <i className={`${item.icon} text-sm sm:text-base ${
                          visible
                            ? item.status === 'warn' ? 'text-red-400' : 'text-emerald-400'
                            : 'text-gray-600'
                        }`}></i>
                      </div>
                      <span className={`text-xs sm:text-sm font-medium ${
                        visible
                          ? item.status === 'warn' ? 'text-red-300' : 'text-white'
                          : 'text-gray-600'
                      }`}>{item.label}</span>
                      {visible && (
                        <span className={`ml-auto text-xs font-mono px-2 py-0.5 rounded-full ${
                          item.status === 'warn'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-emerald-500/20 text-emerald-400'
                        }`}>
                          {item.status === 'warn' ? 'REVIEW' : 'OK'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Progress bar */}
              <div className="mt-4 sm:mt-5 pt-3 sm:pt-4 border-t border-gray-700/50">
                <div className="flex justify-between text-xs text-gray-400 mb-2">
                  <span className="font-mono">Scan Progress</span>
                  <span className="font-mono text-[#00FFD1]">{scanProgress}%</span>
                </div>
                <div className="w-full bg-[#0F1F35] rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-[#00A3E0] to-[#00FFD1] transition-all duration-300"
                    style={{ width: `${scanProgress}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Alert card */}
            <div className={`border rounded-2xl p-4 sm:p-5 transition-all duration-700 ${
              showAlert
                ? 'bg-red-500/10 border-red-500/40 opacity-100 translate-y-0'
                : 'bg-[#0A1628]/50 border-gray-700/30 opacity-40 translate-y-2'
            }`}>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl bg-red-500/20 shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-red-400 text-base sm:text-lg"></i>
                </div>
                <div>
                  <div className="text-xs sm:text-sm font-bold text-red-300 mb-1">Inspection Recommended</div>
                  <div className="text-xs text-gray-400 leading-relaxed">
                    Surface irregularity detected on left wing upper surface near aileron hinge. Recommend visual confirmation before flight.
                  </div>
                  <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-xs bg-red-500/20 text-red-400 border border-red-500/30 rounded-full px-2 sm:px-3 py-1 font-mono">WING — LEFT UPPER</span>
                    <span className="text-xs text-gray-500 font-mono">Confidence: 94%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature bullets - collapses to 1-col on mobile */}
            <div className="grid sm:grid-cols-2 gap-2 sm:gap-3">
              {[
                { icon: 'ri-eye-line', label: 'Visual AI Scan', desc: 'Camera-based surface analysis' },
                { icon: 'ri-map-pin-line', label: 'Issue Pinpointing', desc: 'Exact location flagged on aircraft' },
                { icon: 'ri-history-line', label: 'Inspection Log', desc: 'Every preflight saved & timestamped' },
                { icon: 'ri-shield-check-line', label: 'Go / No-Go', desc: 'Clear readiness summary' },
              ].map((f, i) => (
                <div key={i} className="bg-[#0A1628]/60 border border-gray-700/40 rounded-xl p-3 sm:p-4 hover:border-[#00FFD1]/30 transition-all">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center mb-2">
                    <i className={`${f.icon} text-[#00FFD1] text-lg sm:text-xl`}></i>
                  </div>
                  <div className="text-xs sm:text-sm font-semibold text-white mb-0.5">{f.label}</div>
                  <div className="text-xs text-gray-500">{f.desc}</div>
                </div>
              ))}
            </div>

            {/* Disclaimer */}
            <div className="flex items-start gap-2 sm:gap-3 bg-[#00A3E0]/5 border border-[#00A3E0]/20 rounded-xl p-3 sm:p-4">
              <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-information-line text-[#00A3E0] text-sm sm:text-base"></i>
              </div>
              <p className="text-xs text-gray-400 leading-relaxed">
                WingmanFix assists your preflight — it never replaces it. The pilot in command is always responsible for airworthiness. This tool helps you look more carefully, not less.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom stats row - collapses to 2-col on mobile */}
        <div className="mt-12 sm:mt-16 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {[
            { value: '47', label: 'Inspection Points', sub: 'per standard walkaround', icon: 'ri-focus-3-line', color: 'text-[#00FFD1]' },
            { value: '< 3s', label: 'Detection Speed', sub: 'per surface area', icon: 'ri-timer-flash-line', color: 'text-[#00A3E0]' },
            { value: '94%', label: 'AI Confidence', sub: 'on flagged items', icon: 'ri-brain-line', color: 'text-[#00FFD1]' },
            { value: '100%', label: 'Logged Preflights', sub: 'timestamped & stored', icon: 'ri-archive-line', color: 'text-[#00A3E0]' },
          ].map((stat, i) => (
            <div key={i} className="bg-[#0A1628]/70 border border-gray-700/50 rounded-xl p-4 sm:p-5 text-center hover:border-[#00FFD1]/30 transition-all">
              <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center mx-auto mb-2">
                <i className={`${stat.icon} text-xl sm:text-2xl ${stat.color}`}></i>
              </div>
              <div className={`text-2xl sm:text-3xl font-bold mb-1 ${stat.color}`}>{stat.value}</div>
              <div className="text-xs sm:text-sm text-white font-medium">{stat.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{stat.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}